import React from 'react';
import { fireEvent, render, screen, waitFor } from '@testing-library/react';

import ConfigProvider from 'components/ConfigProvider';

import AccountsField from './AccountsField';
import AdditionalInfoStep from './AdditionalInfoStep';
import UserDetailsStep from './UserDetailsStep';
import { ACCOUNT_STEP, ADDITIONAL_STEP, DETAILS_STEP } from './copy';
import { POPOVER_Z_INDEX, WIZARD_Z_INDEX } from './layers';

/**
 * Every menu inside the wizard has to be told to sit above it.
 *
 * KeyDS gives a Popover `currentZIndex + zIndex`, and currentZIndex comes from the
 * nearest StackingContextProvider — which Wizard never provides. So each of these
 * would otherwise compute 0 and render *behind* the wizard. That is invisible to
 * the tests that only assert content, hence this file.
 *
 * The layer is read off the popover's own container, walking up from the panel:
 * the z-index lands on the Popover's positioned wrapper, not on the content.
 *
 * Note this reads inline styles, not stylesheet rules. jsdom cannot evaluate
 * KeyDS's sibling-combinator CSS at all — getComputedStyle throws inside its
 * selector engine — so nothing here can assert a *stylesheet*-driven appearance.
 * The checkbox's checked mark is one such case, and is browser-only to verify.
 */
const layerAbove = (element) => {
  let node = element;

  while (node && node !== document.body) {
    const { zIndex } = window.getComputedStyle(node);
    if (zIndex && zIndex !== 'auto') return Number(zIndex);
    node = node.parentElement;
  }

  return null;
};

describe('management/Users CreateUserWizard layers', () => {
  it('puts menus above the wizard', () => {
    expect(POPOVER_Z_INDEX).toBeGreaterThan(WIZARD_Z_INDEX);
  });

  it('lifts the Accounts menu above the wizard', async () => {
    render(
      <ConfigProvider>
        <AccountsField
          options={[{ label: 'Alpha Fleet', value: 7 }]}
          selected={[]}
          onChange={() => {}}
          onSearch={() => {}}
        />
      </ConfigProvider>,
    );

    fireEvent.click(
      screen.getByRole('button', { name: new RegExp(ACCOUNT_STEP.label, 'i') }),
    );

    // An option row: something the menu renders and the trigger does not, so
    // finding it means the popover itself is on screen.
    await waitFor(() =>
      expect(screen.getByText('Alpha Fleet')).toBeInTheDocument(),
    );

    expect(layerAbove(screen.getByText('Alpha Fleet'))).toEqual(
      POPOVER_Z_INDEX,
    );
  });

  it('lifts the Job Role dropdown above the wizard', async () => {
    render(
      <ConfigProvider>
        <UserDetailsStep
          draft={{
            dmsId: '',
            email: '',
            firstName: '',
            jobRole: '',
            lastName: '',
            mobile: '',
            mobileCountryCode: '1',
          }}
          jobRoleOptions={[{ id: 'fleet_manager', label: 'Fleet Manager' }]}
          phoneCountryCodeOptions={[{ id: '1', label: '+1' }]}
          onBlurEmail={() => {}}
          onBlurMobile={() => {}}
          onChange={() => {}}
        />
      </ConfigProvider>,
    );

    fireEvent.click(screen.getByLabelText(DETAILS_STEP.jobRole));

    expect(layerAbove(await screen.findByText('Fleet Manager'))).toEqual(
      POPOVER_Z_INDEX,
    );
  });

  it('lifts the Country dropdown above the wizard', async () => {
    render(
      <ConfigProvider>
        <AdditionalInfoStep
          countryOptions={[{ id: 'US', label: 'United States' }]}
          draft={{
            address1: '',
            address2: '',
            city: '',
            countryCode: '',
            fax: '',
            phone: '',
            postalCode: '',
            state: '',
          }}
          stateOptions={[]}
          onChange={() => {}}
          onChangeCountry={() => {}}
        />
      </ConfigProvider>,
    );

    fireEvent.click(screen.getByLabelText(ADDITIONAL_STEP.country));

    expect(layerAbove(await screen.findByText('United States'))).toEqual(
      POPOVER_Z_INDEX,
    );
  });
});
